import { apiClient } from "@/services/apiClient";
import type {
  NotarialRequestDocumentDto,
  NotarialRequestStatus,
} from "@/services/notaryRequestsService";

export interface PlatformRequestListItem {
  id: string;
  requestedByUserId: string;
  applicantName: string | null;
  applicantEmail: string | null;
  status: NotarialRequestStatus;
  requestType: string;
  sourceRequestTitle: string | null;
  acceptedByUserId: string | null;
  acceptedByName: string | null;
  acceptedAt: string | null;
  createdAt: string;
  updatedAt: string;
  documentCount: number;
}

export interface PlatformRequestListResponse {
  items: PlatformRequestListItem[];
  total: number;
}

export function listPlatformRequests(params: {
  status?: NotarialRequestStatus;
  type?: string;
  limit?: number;
  offset?: number;
}): Promise<PlatformRequestListResponse> {
  const searchParams = new URLSearchParams();
  if (params.status) searchParams.set("status", params.status);
  if (params.type) searchParams.set("type", params.type);
  if (params.limit != null) searchParams.set("limit", String(params.limit));
  if (params.offset != null) searchParams.set("offset", String(params.offset));
  const query = searchParams.toString();
  return apiClient
    .get<PlatformRequestListResponse>(`/platform/requests${query ? `?${query}` : ""}`)
    .then((res) => res.data);
}

export interface PlatformNotarialRequestEventDto {
  id: string;
  eventType: string;
  actorUserId: string | null;
  actorDisplayName: string | null;
  actorRole: string | null;
  message: string | null;
  createdAt: string;
}

export type PlatformRequestKind = "notary" | "accountant";

export interface PlatformNotarialRequestDetail {
  /** Present on API responses; when missing, treat as notary (legacy). */
  platformRequestKind?: PlatformRequestKind;
  id: string;
  requestedByUserId: string;
  requesterName: string | null;
  requesterEmail: string | null;
  requesterDocumentType: string | null;
  requesterDocumentNumber: string | null;
  status: NotarialRequestStatus;
  acceptedByUserId: string | null;
  assignedProfessionalName: string | null;
  assignedProfessionalEmail: string | null;
  acceptedAt: string | null;
  certificatePathChoice: string | null;
  missingInfoMessage: string | null;
  missingDocumentNotes: Record<string, string> | null;
  cannotCompleteNotes: string | null;
  pickupAddressSnapshot: string | null;
  deliveryAddressSnapshot: string | null;
  deliveryContactPhone: string | null;
  deliveryContactName: string | null;
  deliveryNotes: string | null;
  fulfillmentInternalNotes: string | null;
  createdAt: string;
  updatedAt: string;
  documents: NotarialRequestDocumentDto[];
  events: PlatformNotarialRequestEventDto[];
  incomeCertificateDetails?: Record<string, unknown> | null;
  money: PlatformRequestMoney | null;
  parentRequestId: string | null;
  parentRequestType: string | null;
  rfqOrigin: PlatformRequestRfqOrigin | null;
  pausedAt: string | null;
  pauseReason: string | null;
  /**
   * Whether this request can still be moved off its professional. DECIDED BY
   * THE SERVER — it is false once a payout line may exist, and restating that
   * status list here would be a second copy of the rule, free to drift from
   * the one RequestReassignService actually enforces.
   */
  reassignable: boolean;
  /**
   * Whether pausing still means anything — false once the request is finished.
   * DECIDED BY THE SERVER, which also enforces it (assertRequestPausable).
   */
  pausable: boolean;
}

/**
 * Mirrors PlatformRequestMoneyDto on the backend. Read-only: every amount is
 * what the checkout ROW recorded, never recomputed here.
 */
export interface PlatformRequestMoney {
  checkoutId: string;
  /** The provider that owns this row — dlocal | mercadopago | bank_transfer. */
  provider: string;
  status: string;
  paidAt: string | null;
  amountMinor: number;
  currency: string;
  consumerServiceFeeMinor: number | null;
  professionalEarningsMinor: number | null;
  /**
   * Support reference ONLY. Set when the order is created, not when it is
   * paid, so a never-paid checkout still has one. Never render it as
   * confirmation of payment.
   */
  providerPaymentId: string | null;
}

export interface PlatformRequestQuote {
  id: string;
  legId: string;
  legDiscipline: "notary" | "accountant" | null;
  professionalUserId: string;
  professionalDisplayName: string | null;
  priceMinor: number;
  currency: string;
  estimatedDays: number | null;
  message: string | null;
  status: string;
  createdAt: string;
}

/** Null for a catalog purchase. Branch on PRESENCE, never on quotes.length. */
export interface PlatformRequestRfqOrigin {
  quoteRequestId: string;
  awardedQuoteId: string | null;
  legId: string | null;
  legDiscipline: "notary" | "accountant" | null;
  /** From the leg, never the checkout scalar. */
  professionalEarningsMinor: number | null;
  quotes: PlatformRequestQuote[];
}

export function getPlatformNotarialRequestDetail(
  requestId: string,
): Promise<PlatformNotarialRequestDetail> {
  return apiClient
    .get<PlatformNotarialRequestDetail>(
      `/platform/notarial-requests/${encodeURIComponent(requestId)}`,
    )
    .then((res) => res.data);
}

export function fetchPlatformNotarialRequestDocumentBlob(
  requestId: string,
  documentId: string,
): Promise<Blob> {
  return apiClient
    .get<Blob>(
      `/platform/notarial-requests/${encodeURIComponent(requestId)}/documents/${encodeURIComponent(documentId)}/file`,
      { responseType: "blob" },
    )
    .then((res) => res.data);
}

/*
  THE FIVE FULFILMENT CALLERS THAT LIVED HERE ARE GONE — spec f462e7d6.

    platformMarkShipmentInTransit
    platformMarkReceivedFromProfessional
    platformNotifyConsumerDelivery
    platformMarkDeliveredWithoutShipmentNotice
    platformMarkShippedCompleted

  Their routes were deleted from platform.controller.ts: the professional
  delivers to the consumer directly, so there is no middle of the chain for an
  operator to move a parcel through. En camino and entregada were not deleted
  but MOVED to the professional's own controller, guarded on the accepting
  professional — which is why there is no platform-side replacement for them
  here. A platform caller would be the weaker of two guards on one transition.
*/

export function platformDeleteNotarialRequest(requestId: string): Promise<void> {
  return apiClient
    .delete(`/platform/notarial-requests/${encodeURIComponent(requestId)}`)
    .then(() => undefined);
}

